import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Link } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';

interface AuthPromptDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AuthPromptDialog: React.FC<AuthPromptDialogProps> = ({ open, onClose, onSuccess }) => {
  const { login, authState } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    try {
      setSubmitting(true);
      await login({ email, password });
      setSubmitting(false);
      onClose();
      onSuccess?.();
    } catch (err) {
      setSubmitting(false);
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(message);
    }
  };

  return (
    <Dialog className="signuptosave" open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Sign in to save and organise products</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create lists and save your favorite products by signing in. If you don't have an account, you can create one from the sign-in page.
        </Typography>
        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <input type="submit" hidden />
        </Box>
        <Box className="create-account-link" sx={{ mt: 1 }}>
          <Link component={RouterLink} to="/register" underline="hover">
            Create an account
          </Link>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button className="cancel-btn" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button className="sign-in-btn" onClick={handleSubmit as any} variant="contained" disabled={submitting || authState.isLoading}>
          {submitting || authState.isLoading ? 'Signing in…' : 'Log In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthPromptDialog;
